import moment from "moment";

/**
 * Lector de FECHAS-DÍA (fechaAlta, fechaReingreso, fechaBaja...): campos que en Mongo son `Date`
 * pero que representan un DÍA de calendario, no un instante.
 *
 * Existe porque en la base coexisten TRES formas de guardar el mismo día, y ningún formateo
 * único las lee bien a todas. Verificado, y documentado también en el backend
 * (Controllers/Prenomina/cierre.controllers.js, sobre por qué el CFDI las lee en UTC):
 *
 *   | Cómo entró                                    | Guardado              | moment.utc | moment local |
 *   |-----------------------------------------------|-----------------------|------------|--------------|
 *   | DatePicker con el mouse                       | 2020-01-02T06:00:00Z  |  02/01 ok  |   02/01 ok   |
 *   | DatePicker con el TECLADO a las 19:30         | 2020-01-03T01:30:00Z  |  03/01 MAL |   02/01 ok   |
 *   | Importador / edición masiva ("YYYY-MM-DD")    | 2020-01-02T00:00:00Z  |  02/01 ok  |   01/01 MAL  |
 *   | Anclada al mediodía (Utils/fechaDia.utils.js) | 2020-01-02T12:00:00Z  |  02/01 ok  |   02/01 ok   |
 *
 * El renglón del teclado es culpa de DatePickerItem.setDate, que le estampa la HORA ACTUAL a la
 * fecha elegida. El del importador es `new Date("YYYY-MM-DD")`, que Mongoose castea a medianoche
 * UTC. `fechaReingreso` sólo la escriben los importadores, así que siempre cae en ese renglón.
 *
 * La regla: una hora UTC de exactamente 00:00:00.000 sólo la produce el camino del importador
 * (una captura hecha en el navegador desde México nunca cae ahí, porque el offset es negativo),
 * así que ese caso se lee en UTC y todo lo demás se lee como el instante local que es. Con eso
 * las cuatro formas dan el día correcto.
 *
 * Limitación consciente: la heurística supone navegador con offset negativo (México), que es el
 * caso de este ERP. Lo que de verdad cierra el tema es anclar al mediodía AL GUARDAR — ver el
 * patrón de Components/FilaHospedajeVenta.js: mandar "YYYY-MM-DD" y anclar con parsearFechaDia.
 */
const esMedianocheUtc = (d) =>
  d.getUTCHours() === 0 &&
  d.getUTCMinutes() === 0 &&
  d.getUTCSeconds() === 0 &&
  d.getUTCMilliseconds() === 0;

/** El `moment` en la zona correcta para leer el día. `null` si no hay fecha o no es válida. */
export const momentDeFechaDia = (f) => {
  if (!f) return null;
  const d = new Date(f);
  if (isNaN(d.getTime())) return null;
  return esMedianocheUtc(d) ? moment.utc(d) : moment(d);
};

/**
 * Clave "YYYY-MM-DD" del día. Es la moneda segura para COMPARAR o restar dos fechas-día: los
 * moment que devuelve momentDeFechaDia pueden venir en modos distintos (uno en UTC y otro en
 * local), y restarlos directo mete el offset en la cuenta.
 */
export const claveFechaDia = (f) => {
  const m = momentDeFechaDia(f);
  return m ? m.format("YYYY-MM-DD") : null;
};

/** Días completos entre dos fechas-día, inclusivo del primer día. `null` si falta alguna. */
export const diasEntreFechasDia = (desde, hasta) => {
  const a = claveFechaDia(desde);
  const b = claveFechaDia(hasta);
  if (!a || !b) return null;
  return moment(b, "YYYY-MM-DD").diff(moment(a, "YYYY-MM-DD"), "days") + 1;
};

/**
 * Convierte lo que trae un DatePicker en lo que debe viajar al backend para una FECHA-DÍA:
 * "YYYY-MM-DD", nunca el moment tal cual.
 *
 * El DatePicker le pega una hora a la fecha elegida (00:00 si se eligió con el mouse, la hora
 * ACTUAL si se capturó con el teclado, ver DatePickerItem.setDate). Serializar eso a ISO manda un
 * instante que el servidor puede leer en otro día. Con "YYYY-MM-DD" no hay nada que interpretar y
 * el backend lo ancla al mediodía con parsearFechaDia (Utils/fechaDia.utils.js).
 *
 * Mismo patrón que hospedajeParaPayload en Components/FilaHospedajeVenta.js.
 * Devuelve `null` —no `undefined`— cuando el campo se vació: el backend hace `$set: req.body` y
 * `JSON.stringify` borra las claves con `undefined`, así que sin el null explícito el campo se
 * quedaría con el valor viejo en Mongo.
 */
export const fechaDiaParaPayload = (v) => {
  if (!v) return null;
  if (typeof v.format === "function") return v.format("YYYY-MM-DD");
  return claveFechaDia(v);
};

/** El día en DD/MM/YYYY, que es como se lee en toda la aplicación. */
const fmtFechaDia = (f, vacio = "—") => {
  const m = momentDeFechaDia(f);
  return m ? m.format("DD/MM/YYYY") : vacio;
};

export default fmtFechaDia;
